import type { User } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { Avatar } from './avatar.js';

export function UserPicker({
  value,
  onChange,
  placeholder,
  allowClear = true,
}: {
  value: User | null;
  onChange: (user: User | null) => void;
  placeholder?: string;
  allowClear?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const usersQuery = useQuery({
    queryKey: ['users', search],
    // #116: Mit Suchbegriff filtert der Server auf aktive Nutzer; ohne ihn liefert er ALLE (für die
    // ID→Name-Auflösung) — dann fällt der Inaktive hier heraus, er ist nicht auswählbar.
    queryFn: async () => {
      const res = await api.listUsers(search || undefined);
      return { ...res, items: res.items.filter((u) => u.active !== false) };
    },
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2 py-1 rounded border bg-white hover:bg-slate-50 text-sm min-w-[180px]"
      >
        {value ? (
          <>
            <Avatar user={value} size={20} />
            <span className="truncate">{value.name}</span>
          </>
        ) : (
          <span className="text-slate-500">{placeholder ?? t('picker.placeholder')}</span>
        )}
        <span className="ml-auto text-slate-400 text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-72 bg-white border rounded shadow-lg max-h-80 overflow-hidden flex flex-col">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('picker.search')}
            className="px-2 py-1.5 text-sm border-b outline-none"
          />
          <ul className="overflow-y-auto flex-1">
            {allowClear && value && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 border-b"
                >
                  {t('picker.clear')}
                </button>
              </li>
            )}
            {usersQuery.isLoading && (
              <li className="px-3 py-2 text-sm text-slate-500">{t('common.loading')}</li>
            )}
            {usersQuery.data?.items.map((u) => (
              <li key={u.user_id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(u);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 ${
                    value?.user_id === u.user_id ? 'bg-slate-100' : ''
                  }`}
                >
                  <Avatar user={u} size={28} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  </div>
                </button>
              </li>
            ))}
            {usersQuery.data && usersQuery.data.items.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500">{t('picker.empty')}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
